import type { StoredVaultEntry, VaultEntryKind, VaultScopeType } from "./persistence";

export interface PendingVaultRefDraftLike {
  kind: VaultEntryKind;
  key: string;
  scopeType: VaultScopeType;
  scopeServerId?: string;
  description?: string;
  value?: string;
}

export function dedupePendingVaultDrafts<T extends PendingVaultRefDraftLike>(drafts: T[]): T[] {
  const deduped = new Map<string, T>();
  for (const draft of drafts) {
    const key = draft.key.trim();
    if (!key) continue;
    const scopeServerId = draft.scopeType === "server" ? (draft.scopeServerId ?? "") : "";
    deduped.set(`${draft.kind}:${draft.scopeType}:${scopeServerId}:${key}`, {
      ...draft,
      key,
    });
  }
  return [...deduped.values()];
}

export function findMissingVaultRefDrafts<T extends PendingVaultRefDraftLike>(
  drafts: T[],
  entries: Array<Pick<StoredVaultEntry, "kind" | "key" | "scopeType" | "scopeServerId">>,
  serverId?: string,
): T[] {
  const missing: T[] = [];

  for (const draft of dedupePendingVaultDrafts(drafts)) {
    const exists = entries.some((entry) => matchesVaultDraft(entry, draft, serverId));
    if (!exists) {
      missing.push(draft);
    }
  }

  return missing;
}

function matchesVaultDraft(
  entry: Pick<StoredVaultEntry, "kind" | "key" | "scopeType" | "scopeServerId">,
  draft: PendingVaultRefDraftLike,
  serverId?: string,
): boolean {
  if (entry.kind !== draft.kind || entry.key !== draft.key) {
    return false;
  }

  if (draft.kind === "secret" && draft.scopeType === "global") {
    return entry.scopeType === "global"
      || (entry.scopeType === "server" && entry.scopeServerId === serverId);
  }

  return entry.scopeType === draft.scopeType
    && (entry.scopeServerId ?? "") === (draft.scopeServerId ?? "");
}
